import { 
  collection, 
  getDocs, 
  query, 
  orderBy, 
  QueryDocumentSnapshot, 
  DocumentData,
  where,
  limit,
  startAfter,
  getCountFromServer,
  QueryConstraint
} from "firebase/firestore";
import Failure from "../../../core/Failure";
import { firestore } from "../../../firebase.config";
import { IPlan } from "@leedpulse-monorepo/interfaces";
import { EProductType } from "@leedpulse-monorepo/enums";
import { FIREBASE_COLLECTIONS } from "@leedpulse-monorepo/consts";

interface PlanOptions {
  product: EProductType;
}

async function getPlans(
  offset: number | undefined,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  options?: PlanOptions
): Promise<{
  plans: IPlan[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
  productCounts?: {
    [EProductType.LEEDPULSE]: number;
    [EProductType.LEEDFLOW]: number;
  }
}> {
  try {
    const db = firestore;
    const plansCollection = collection(db, FIREBASE_COLLECTIONS.PLANS);

    const plansConstraints = [];

    // Add product option
    if (options?.product) {
      plansConstraints.push(where('product', '==', options.product));
    }

    const queryConstraints: QueryConstraint[] = [
      ...plansConstraints,
      orderBy("name", "asc"),
    ];

    if (lastVisible) {
      queryConstraints.push(startAfter(lastVisible));
    }

    if (offset) {
      queryConstraints.push(limit(offset));
    }

    const results = await getDocs(query(plansCollection, ...queryConstraints));

    const plans: IPlan[] = [];
    results.docs.forEach((doc) => {
      plans.push(doc.data() as IPlan);
    });

    const plansSnapshot = await getCountFromServer(plansCollection);

    if (options) {
      const leedpulsePlansSnapshot = await getCountFromServer(
        query(
          plansCollection,
          where('product', '==', EProductType.LEEDPULSE)
        )
      );

      const leedflowPlansSnapshot = await getCountFromServer(
        query(
          plansCollection,
          where('product', '==', EProductType.LEEDFLOW)
        )
      );

      return {
        plans,
        lastVisible: results.docs[results.docs.length - 1],
        totalCollectionCount: plansSnapshot.data().count ?? 0,
        productCounts: {
          [EProductType.LEEDPULSE]: leedpulsePlansSnapshot.data().count ?? 0,
          [EProductType.LEEDFLOW]: leedflowPlansSnapshot.data().count ?? 0
        }
      }
    }

    return {
      plans,
      lastVisible: results.docs[results.docs.length - 1],
      totalCollectionCount: plansSnapshot.data().count ?? 0,
    };
  } catch (e) {
    console.log("error: ", e);
    throw new GetPlansFailure();
  }
}

export class GetPlansFailure extends Failure {
  constructor(message = "Unknown error", code = "unknown-error") {
    super(message, code);
  }
}

export default getPlans;
